import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DELETE_ALL_ALERTS_ACTION,
  DELETE_ONE_ALERT_ACTION,
  GET_ALL_ALERTS_ACTION,
  GET_USER_MESSAGES,
  deleteAllAlerts,
  deleteOneAlert,
  getAlerts,
  getUserMessages,
  hideMessage,
  selectContentTypeInbox,
  showMessage,
} from '../actions';
import { createLoadingSelector } from '../api/selectors';
import { Badge, Column, Row, Screen, Spinner, Tabs, Typography } from '@tempmee/react';
import { borderRadius } from '@tempmee/tailwind';
import {
  enableChatsUnread,
  enableLastJobPostedReminders,
  enablePaymentExpirationReminders,
  enableProfileCompletenessReminders,
} from '../growthbook';
import Alerts from './Alerts';
import { Chat } from './Inbox/Chat/Chat';
import { useChatsUnreadCount } from './Inbox/Chat/hooks/useChatsUnreadCount';
import { useInbox } from './Inbox/hooks/useInbox';
import { useNotificationCount } from './Inbox/Notification/hooks/useNotificationCount';
import { Notification } from './Inbox/Notification/Notification';
import { useTaskDeletion } from './Inbox/Notification/Tasks/hooks/useTaskDeletion';
import { NotificationType } from './Inbox/Notification/Type';
import Messages from './messages/Messages';
import history from '~/browserHistory';

const LIMIT_VALUE = 15;

export default function Inbox({ isRootComponent = true }) {
  const selectedContent = useSelector((state) => state.messages.selectedContent);
  const conversationCount = useSelector((state) => state.user.user.conversationCount);

  const isFinalPage = useSelector((state) => state.messages.isFinalPage);
  const isFinalPageAlerts = useSelector((state) => state.alerts.isFinalPage);

  const isActiveRequest = useSelector((state) => state.messages.isActiveRequest);
  const isActiveRequestAlerts = useSelector((state) => state.alerts.isActiveRequest);

  const selectedOfficeId = useSelector((state) => state.user.selectedOfficeId);

  const totalUnread = useChatsUnreadCount();
  const totalNotifications = useNotificationCount();
  const { handleDeleteAll, handleDeleteOne } = useTaskDeletion();

  const isEnableChatUnread = useRef(enableChatsUnread()).current;
  const isEnableLastJobPostedReminders = useRef(enableLastJobPostedReminders()).current;
  const isEnablePaymentExpirationReminders = useRef(enablePaymentExpirationReminders()).current;
  const isEnableProfileCompletenessReminders = useRef(enableProfileCompletenessReminders()).current;

  const isEnableNotificationReminder =
    isEnableLastJobPostedReminders ||
    isEnablePaymentExpirationReminders ||
    isEnableProfileCompletenessReminders;

  const isAlertContent = selectedContent === NotificationType.Alert;
  const isMessageContent = selectedContent === NotificationType.Message;

  const dispatch = useDispatch();

  const loadingSelector = createLoadingSelector([
    GET_ALL_ALERTS_ACTION,
    GET_USER_MESSAGES,
    DELETE_ALL_ALERTS_ACTION,
    DELETE_ONE_ALERT_ACTION,
  ]);

  const { page } = useSelector((store) => store.messages.searchParams);
  const searchParamsAlerts = useSelector((store) => store.alerts.searchParams);

  const isLoading = useSelector((state) => loadingSelector(state));
  const refMessages = useRef(null);
  const refAlerts = useRef(null);

  const handleScrollMessages = () => {
    const messagesScroll = refMessages?.current;

    const userScrollHeight = messagesScroll?.scrollHeight - messagesScroll?.scrollTop;
    const clientHeight = messagesScroll?.clientHeight;

    if (userScrollHeight <= clientHeight && !isActiveRequest) {
      dispatch(getUserMessages({ page: page + 1, limit: LIMIT_VALUE }));
    }
  };

  const handleScrollAlerts = () => {
    const alertsScroll = refAlerts.current;

    const userScrollHeight = alertsScroll?.scrollHeight - alertsScroll?.scrollTop;
    const clientHeight = alertsScroll?.clientHeight + 20;

    if (userScrollHeight <= clientHeight && !isActiveRequestAlerts && !isFinalPageAlerts) {
      dispatch(getAlerts({ page: searchParamsAlerts?.page + 1, limit: LIMIT_VALUE }));
    }
  };

  const { getMessages, getNotifications } = useInbox();

  useEffect(() => {
    if (isAlertContent && !isEnableNotificationReminder) {
      dispatch(getAlerts({ page: 1, limit: LIMIT_VALUE }));
    } else if (isMessageContent && !isEnableChatUnread) {
      dispatch(getUserMessages({ page: 1, limit: LIMIT_VALUE }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfficeId]);

  const selectInboxType = (type) => {
    if (type === 'alerts') {
      getNotifications();
      dispatch(selectContentTypeInbox({ payload: 'alerts' }));
    } else if (type === 'messages') {
      getMessages();
      dispatch(selectContentTypeInbox({ payload: 'messages' }));
    }
  };

  const deleteAll = () => {
    dispatch(
      showMessage({
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete all your messages?',
        isError: true,
        popupProps: {
          leftButtonText: 'Go Back',
          leftButtonAction: () => dispatch(hideMessage()),
          rightButtonText: 'Remove',
          rightButtonAction: () => {
            dispatch(deleteAllAlerts());
            dispatch(hideMessage());
          },
        },
      }),
    );
  };

  const selectedAlert = (job) => {
    // TODO: - follow new alert schema
    if (job.job_path === 'DNT_OFFER') {
      history.push(`/dashboard/job/${job.joboffer_id}`, {
        jobId: job.joboffer_id,
      });
    }
  };

  const deleteOneAlertSubmit = (alertId) => {
    dispatch(deleteOneAlert({ alertId }));
  };

  const selectedMessage = (job) => {
    history.push(`/dashboard/chat/${job.id}`, { jobId: job.id });
  };

  const renderAlertContent = () => {
    if (isEnableNotificationReminder) {
      return <Notification />;
    }

    return (
      <Column
        ref={refAlerts}
        onScroll={handleScrollAlerts}
        style={{ overflowY: 'scroll', maxHeight: 500 }}
      >
        <Alerts
          isLoading={isLoading}
          selectedJobs={selectedAlert}
          deleteAll={handleDeleteAll}
          deleteOneAlert={handleDeleteOne}
        />

        {!isFinalPageAlerts && isLoading ? (
          <Row align="center" justify="center" style={{ width: '100%', height: 100 }}>
            <Spinner size="md" />
          </Row>
        ) : null}
      </Column>
    );
  };

  const renderMessageContent = () => {
    if (isEnableChatUnread) {
      return <Chat isRootComponent={isRootComponent} />;
    }

    return (
      <Column
        ref={refMessages}
        onScroll={handleScrollMessages}
        style={{ overflowY: 'scroll', maxHeight: 500 }}
      >
        <Messages
          isLoading={isLoading}
          selectedJobs={selectedMessage}
          deleteAll={deleteAll}
          deleteOneAlert={deleteOneAlertSubmit}
        />
        {!isFinalPage && isLoading ? (
          <Row align="center" justify="center" style={{ width: '100%', height: 100 }}>
            <Spinner size="md" />
          </Row>
        ) : null}
      </Column>
    );
  };

  const content = (
    <Tabs value={selectedContent || 'alerts'} onValueChange={selectInboxType}>
      <Tabs.List variant="underline">
        <Tabs.Tab
          value="alerts"
          label={
            isEnableNotificationReminder
              ? totalNotifications > 99
                ? '99+'
                : String(totalNotifications)
              : undefined
          }
        >
          Notifications
        </Tabs.Tab>
        <Tabs.Tab
          value="messages"
          label={isEnableChatUnread ? (totalUnread > 99 ? '99+' : String(totalUnread)) : undefined}
        >
          {!isEnableChatUnread && conversationCount > 0 ? (
            <Row align="center" gap={2}>
              Chats
              <Badge dot variant="error" />
            </Row>
          ) : (
            'Chats'
          )}
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="alerts">
        <Column style={{ flex: 1, marginBlock: 20, width: '100%', maxHeight: '100%' }}>
          {renderAlertContent()}
        </Column>
      </Tabs.Panel>
      <Tabs.Panel value="messages">
        <Column style={{ flex: 1, marginBlock: 20, width: '100%', maxHeight: '100%' }}>
          {renderMessageContent()}
        </Column>
      </Tabs.Panel>
    </Tabs>
  );

  if (isRootComponent) {
    return (
      <Screen variant="standard">
        <Screen.Header title="Inbox" />
        <Screen.Body>{content}</Screen.Body>
      </Screen>
    );
  }

  return (
    <Column className="inbox" style={{ borderRadius: borderRadius.pill }}>
      <Column style={{ width: '100%', padding: '25px 25px 15px', flex: 1 }}>
        <Typography
          size="heading_2"
          bold
          style={{ marginBottom: 0, marginLeft: 30, marginRight: 20, textAlign: 'center' }}
        >
          Inbox
        </Typography>
        {content}
      </Column>
    </Column>
  );
}
